import { GetMcaEstimatedScheduleResponse } from '@iwoca/lapi-client/edge';
import { Pill } from '@iwoca/orion';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import { getOriginalPricing } from './getOriginalPricing';
import messages from './RBLLoanSummary.messages';
import styles from './RBLLoanSummary.module.css';
import { MoneyNoDecimals } from '../../../../../ui/moneyNoDecimals';
import { MoneyWithDecimals } from '../../../../../ui/moneyWithDecimals';
import { Rate } from '../../../../../ui/rate';

type WithdrawalSummaryProps = {
    schedule: GetMcaEstimatedScheduleResponse;
    loading: boolean;
    drawdownAmount: number;
};

export const WithdrawalSummary = ({
    schedule,
    loading,
    drawdownAmount
}: WithdrawalSummaryProps) => {
    const summary = schedule?.data.summary;

    const originalFeeAmount = getOriginalPricing({
        numberOfRepayments: schedule?.data.estimated_schedule.repayments.length,
        totalCreditAmount: summary.total_credit_amount,
        totalPayable: summary.total_payable
    });

    // only highlight upfront fee savings > 2.5%, i.e. where the new fee is 97.5% or below the current fee.
    const hasDiscountedPricingDE =
        summary.funding_fee <= 0.975 * originalFeeAmount;

    return (
        <div className={styles.loanSummary}>
            <div className={styles.summaryRow}>
                <FormattedMessage {...messages.withdrawalAmountConfirmation} />
                <MoneyNoDecimals
                    className={cn(styles.subText, {
                        [styles.loading]: loading
                    })}
                    value={drawdownAmount}
                />
            </div>

            {hasDiscountedPricingDE && (
                <div className={styles.discountPill}>
                    <Pill>
                        <FormattedMessage
                            {...messages.discountedOffer}
                            values={{
                                amount: (
                                    <span>
                                        &thinsp;
                                        <MoneyWithDecimals
                                            className={cn({
                                                [styles.loading]: loading
                                            })}
                                            value={
                                                originalFeeAmount -
                                                summary.funding_fee
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </Pill>
                </div>
            )}

            <div className={styles.summaryRow}>
                <div className={styles.feeColumn}>
                    <div>
                        <FormattedMessage {...messages.feeAmount} />
                    </div>
                    <div className={styles.feeNoInterestCopy}>
                        <FormattedMessage {...messages.noInterest} />
                    </div>
                </div>

                <div className={styles.feeAmountColumn}>
                    {hasDiscountedPricingDE && (
                        <MoneyWithDecimals
                            className={cn(
                                styles.originalAmountWithStrikethrough,
                                styles.subText,
                                {
                                    [styles.loading]: loading
                                }
                            )}
                            value={originalFeeAmount}
                        />
                    )}
                    <MoneyWithDecimals
                        className={cn(styles.subText, {
                            [styles.loading]: loading
                        })}
                        value={summary.funding_fee}
                    />

                    <div
                        className={cn(styles.feePercentage, {
                            [styles.loading]: loading
                        })}
                    >
                        <Rate
                            className={styles.feePercentageAmount}
                            value={
                                summary.funding_fee /
                                summary.total_credit_amount
                            }
                        />{' '}
                        <FormattedMessage {...messages.feeProportion} />
                    </div>
                </div>
            </div>

            <div className={styles.summaryRow}>
                <FormattedMessage {...messages.totalRepayment} />

                <MoneyNoDecimals
                    className={cn(styles.subText, {
                        [styles.loading]: loading
                    })}
                    value={summary.total_payable}
                />
            </div>
        </div>
    );
};
